import cn from 'classnames';
import { forwardRef } from 'react';

import useControlledValue from '../../../../utils/hooks/use-controlled-value';
import { Tag } from '../tag';

import type { ForwardedRef, KeyboardEvent, MouseEvent, ReactElement } from 'react';

import type { InputTagProps } from './input-tag.types';

import './input-tag.scss';

/**
 * Input tags are used in auto complete and search inputs.
 *
 * ### Usage
 *
 * ```tsx
 * import { InputTag } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return <InputTag>Banana</InputTag>;
 * }
 * ```
 */
const InputTag = forwardRef((props: InputTagProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    defaultSelected = false,
    disabled = false,
    dismissible = false,
    icon,
    onChange,
    onDismiss,
    readOnly,
    selected,
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const [controlledSelected, setControlledSelected] = useControlledValue({
    controlledValue: selected,
    defaultValue: defaultSelected,
    name: 'InputTag',
    state: 'selected',
  });

  const handleChange = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>): void => {
    if (disabled) {
      return;
    }

    const newState = readOnly ? !!controlledSelected : !controlledSelected;
    setControlledSelected(newState);
    onChange?.(event, newState);
  };

  const classes = cn(
    'rbui-input-tag',
    {
      'rbui-input-tag--disabled': disabled,
      'rbui-input-tag--selected': !disabled && controlledSelected,
      'rbui-input-tag--unselected': !disabled && !controlledSelected,
    },
    className,
  );

  return (
    <Tag
      aria-selected={disabled ? undefined : controlledSelected}
      bordered={!controlledSelected && !disabled}
      className={classes}
      disabled={disabled}
      dismissible={dismissible && !disabled}
      icon={icon}
      onClick={handleChange}
      onDismiss={onDismiss}
      readOnly={readOnly}
      ref={forwardedRef}
      {...rest}
    >
      {children}
    </Tag>
  );
});

InputTag.displayName = 'InputTag';

export default InputTag;
